// Dynamic mock system for the attendance widget.
// Instead of pre-built snapshots, this uses scenario configs + a live marks array.
// Mutations update the marks in-place, and the query rebuilds context from them.
//
// Activated by: ?mock_scenario=<key>  (add &mutation_mock=<key> for error testing)
// Browser refresh resets all marks to pending — always starts clean.

import {
  TimeAttendanceMarkStatus,
  TimeAttendanceMarkType,
  TimeShiftType,
} from '@/generated/graphql/types';

import type {
  TDailyMarkingContext,
  TExpectedMark,
  TShiftContext,
} from './types';

// ---------------------------------------------------------------------------
// URL param names
// ---------------------------------------------------------------------------

export const MOCK_SCENARIO_PARAM = 'mock_scenario';
export const MUTATION_MOCK_PARAM = 'mutation_mock';

// ---------------------------------------------------------------------------
// Time helpers — all relative to "now" so mocks are always realistic
// ---------------------------------------------------------------------------

export const MOCK_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

const formatIso = (d: Date): string => {
  const offset = d.getTimezoneOffset();
  const sign = offset <= 0 ? '+' : '-';
  const abs = Math.abs(offset);
  const oh = String(Math.floor(abs / 60)).padStart(2, '0');
  const om = String(abs % 60).padStart(2, '0');

  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');

  return `${y}-${mo}-${da}T${hh}:${mm}:${ss}${sign}${oh}:${om}`;
};

const minutesFromNow = (minutes: number): string =>
  formatIso(new Date(Date.now() + minutes * 60_000));

const minutesAgo = (minutes: number): string => minutesFromNow(-minutes);

export const todayDateString = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');

  return `${y}-${mo}-${da}`;
};

export const nowIso = (): string => formatIso(new Date());

// ---------------------------------------------------------------------------
// Shift constants (in minutes)
// ---------------------------------------------------------------------------

const SHIFT_DURATION = 540; // 9h total shift window
const WORK_MINUTES = 480; // 8h actual work (9h - 1h break)
const BREAK_OFFSET = 240; // break starts 4h after shift entry
const BREAK_DURATION = 60; // 1h lunch
const TOLERANCE = 15; // 15 min tolerance before/after

// ---------------------------------------------------------------------------
// Shift builders — `startedAgo`: how many minutes ago the shift's entry_time was
// ---------------------------------------------------------------------------

const standardShift = (startedAgo: number): TShiftContext => ({
  shift_name: 'Turno Oficina',
  shift_type: TimeShiftType.NonRotating,
  is_working_day: true,
  entry_time: minutesAgo(startedAgo),
  exit_time: minutesFromNow(SHIFT_DURATION - startedAgo),
  effective_shift_start: minutesAgo(startedAgo + TOLERANCE),
  effective_shift_end: minutesFromNow(SHIFT_DURATION - startedAgo + TOLERANCE),
  expected_minutes: WORK_MINUTES,
  break_time_slots: [
    {
      break_label: 'Almuerzo',
      start_time: minutesFromNow(BREAK_OFFSET - startedAgo),
      end_time: minutesFromNow(BREAK_OFFSET + BREAK_DURATION - startedAgo),
      effective_break_start: minutesFromNow(
        BREAK_OFFSET - TOLERANCE - startedAgo,
      ),
      effective_break_end: minutesFromNow(
        BREAK_OFFSET + BREAK_DURATION + TOLERANCE - startedAgo,
      ),
      duration_minutes: BREAK_DURATION,
      position: 1,
    },
  ],
});

const noBreakShift = (startedAgo: number): TShiftContext => ({
  ...standardShift(startedAgo),
  shift_name: 'Turno Sin Descanso',
  expected_minutes: SHIFT_DURATION,
  break_time_slots: [],
});

const rotatingShift = (startedAgo: number): TShiftContext => ({
  ...standardShift(startedAgo),
  shift_name: 'Turno Rotativo A',
  shift_type: TimeShiftType.Rotating,
});

const rotatingNoBreakShift = (startedAgo: number): TShiftContext => ({
  ...noBreakShift(startedAgo),
  shift_name: 'Turno Rotativo B',
  shift_type: TimeShiftType.Rotating,
});

// ---------------------------------------------------------------------------
// Schedule builders — approximate next/previous day shifts
// ---------------------------------------------------------------------------

const DAY_MINUTES = 24 * 60;

const nextDaySchedule = () => ({
  shift_name: 'Turno Oficina',
  shift_type: TimeShiftType.NonRotating,
  is_working_day: true,
  entry_time: minutesFromNow(DAY_MINUTES),
  exit_time: minutesFromNow(DAY_MINUTES + SHIFT_DURATION),
  expected_minutes: WORK_MINUTES,
});

const prevDaySchedule = () => ({
  shift_name: 'Turno Oficina',
  shift_type: TimeShiftType.NonRotating,
  is_working_day: true,
  entry_time: minutesAgo(DAY_MINUTES),
  exit_time: minutesAgo(DAY_MINUTES - SHIFT_DURATION),
  expected_minutes: WORK_MINUTES,
});

// ---------------------------------------------------------------------------
// Mark builder — all marks start as PENDING with no marked_at
// ---------------------------------------------------------------------------

const pendingMark = (markType: TimeAttendanceMarkType): TExpectedMark => ({
  mark_type: markType,
  status: TimeAttendanceMarkStatus.Pending,
  marked_at: null,
});

// The 4 standard marks in chronological order.
// Used to derive next_pending_mark and build initial marks.
const MARK_ORDER = [
  TimeAttendanceMarkType.Entry,
  TimeAttendanceMarkType.BreakStart,
  TimeAttendanceMarkType.BreakEnd,
  TimeAttendanceMarkType.Exit,
] as const;

const ALL_FOUR_MARKS = MARK_ORDER.map(pendingMark);
const ENTRY_EXIT_MARKS = [
  pendingMark(TimeAttendanceMarkType.Entry),
  pendingMark(TimeAttendanceMarkType.Exit),
];

// ---------------------------------------------------------------------------
// Scenario configs
//
// Each scenario defines the shift type and initial marks.
// Shift times are computed relative to "now" on page load (startedAgo=5
// means the shift started 5 min ago — user clicking entry NOW is nearly on time).
// ---------------------------------------------------------------------------

type TMockScenarioConfig = {
  label: string;
  buildShiftContext: () => TShiftContext | null;
  buildInitialMarks: () => TExpectedMark[];
  buildNextSchedule: () => ReturnType<typeof nextDaySchedule> | null;
  buildPreviousSchedule: () => ReturnType<typeof prevDaySchedule> | null;
};

export const MOCK_SCENARIOS: Record<string, TMockScenarioConfig> = {
  // Standard office shift with 1h lunch break — full 4-mark flow
  nr_with_break: {
    label: 'Non-rotating with break (Entry → Break → Exit)',
    buildShiftContext: () => standardShift(5),
    buildInitialMarks: () => ALL_FOUR_MARKS.map(m => ({ ...m })),
    buildNextSchedule: nextDaySchedule,
    buildPreviousSchedule: prevDaySchedule,
  },

  // Standard shift without breaks — 2-mark flow (Entry → Exit)
  nr_no_break: {
    label: 'Non-rotating without break (Entry → Exit)',
    buildShiftContext: () => noBreakShift(5),
    buildInitialMarks: () => ENTRY_EXIT_MARKS.map(m => ({ ...m })),
    buildNextSchedule: nextDaySchedule,
    buildPreviousSchedule: prevDaySchedule,
  },

  // Rotating shift with break — same flow as nr_with_break but different shift type
  rotating_with_break: {
    label: 'Rotating with break (Entry → Break → Exit)',
    buildShiftContext: () => rotatingShift(5),
    buildInitialMarks: () => ALL_FOUR_MARKS.map(m => ({ ...m })),
    buildNextSchedule: nextDaySchedule,
    buildPreviousSchedule: prevDaySchedule,
  },

  // Rotating shift without breaks
  rotating_no_break: {
    label: 'Rotating without break (Entry → Exit)',
    buildShiftContext: () => rotatingNoBreakShift(5),
    buildInitialMarks: () => ENTRY_EXIT_MARKS.map(m => ({ ...m })),
    buildNextSchedule: nextDaySchedule,
    buildPreviousSchedule: prevDaySchedule,
  },

  // No shift assigned — backend returns 4 standard marks, all get REGISTERED status.
  // Timer counts everything as out-of-schedule (no expected window).
  no_shift: {
    label: 'No shift assigned (4 marks, all REGISTERED)',
    buildShiftContext: () => null,
    buildInitialMarks: () => ALL_FOUR_MARKS.map(m => ({ ...m })),
    buildNextSchedule: () => null,
    buildPreviousSchedule: () => null,
  },

  // Rest day — shift exists but is_working_day=false.
  // The user can still mark attendance (e.g. overtime).
  rest_day: {
    label: 'Rest day (is_working_day=false, all REGISTERED)',
    buildShiftContext: () => ({
      shift_name: 'Turno Oficina',
      shift_type: TimeShiftType.NonRotating,
      is_working_day: false,
      entry_time: null,
      exit_time: null,
      effective_shift_start: null,
      effective_shift_end: null,
      expected_minutes: null,
      break_time_slots: [],
    }),
    buildInitialMarks: () => ALL_FOUR_MARKS.map(m => ({ ...m })),
    buildNextSchedule: nextDaySchedule,
    buildPreviousSchedule: prevDaySchedule,
  },
};

// ---------------------------------------------------------------------------
// Shared mock state (module-level)
//
// Stores the scenario's shift context (immutable after init) and the marks
// array (mutated by the mutation mock). The query mock subscribes to changes.
// ---------------------------------------------------------------------------

type TMockState = {
  scenarioKey: string;
  shiftContext: TShiftContext | null;
  marks: TExpectedMark[];
  nextSchedule: ReturnType<typeof nextDaySchedule> | null;
  previousSchedule: ReturnType<typeof prevDaySchedule> | null;
};

let _state: TMockState | undefined;
let _listener: (() => void) | undefined;

/** Called once on page load to initialize the mock from the scenario config.
 * Builds shift context and marks with timestamps relative to "now". */
export const initMockScenario = (
  scenarioKey: string,
): TMockState | undefined => {
  const scenario = MOCK_SCENARIOS[scenarioKey];

  if (!scenario) {
    return undefined;
  }

  _state = {
    scenarioKey,
    shiftContext: scenario.buildShiftContext(),
    marks: scenario.buildInitialMarks(),
    nextSchedule: scenario.buildNextSchedule(),
    previousSchedule: scenario.buildPreviousSchedule(),
  };

  return _state;
};

export const getMockState = (): TMockState | undefined => _state;

/** Updates a mark in the shared state and notifies the query mock to re-render. */
export const updateMockMark = (
  markType: TimeAttendanceMarkType,
  status: TimeAttendanceMarkStatus,
  markedAt: string,
): void => {
  if (!_state) {
    return;
  }

  _state = {
    ..._state,
    marks: _state.marks.map(m =>
      m.mark_type === markType ? { ...m, status, marked_at: markedAt } : m,
    ),
  };

  // Notify the query mock that marks changed → triggers re-render
  _listener?.();
};

export const subscribeMockState = (listener: () => void): (() => void) => {
  _listener = listener;

  return () => {
    _listener = undefined;
  };
};

// ---------------------------------------------------------------------------
// Context builder — assembles a TDailyMarkingContext from the current state
// ---------------------------------------------------------------------------

/** Finds the first pending mark in chronological order.
 * This drives which button the UI shows as primary (e.g. "Registrar ingreso"). */
const deriveNextPendingMark = (
  marks: TExpectedMark[],
): TimeAttendanceMarkType | null => {
  for (const markType of MARK_ORDER) {
    const mark = marks.find(m => m.mark_type === markType);

    if (mark && !mark.marked_at) {
      return markType;
    }
  }

  return null;
};

/** Day is complete when Exit has been registered — even if breaks were skipped. */
const deriveAllMarksCompleted = (marks: TExpectedMark[]): boolean => {
  const exitMark = marks.find(m => m.mark_type === TimeAttendanceMarkType.Exit);

  return !!exitMark?.marked_at;
};

/** Builds a full TDailyMarkingContext from the current mock state.
 * Called by the query mock on every render and by the mutation mock for the response. */
export const buildMockContext = (state: TMockState): TDailyMarkingContext => {
  const allCompleted = deriveAllMarksCompleted(state.marks);

  return {
    shift_context: state.shiftContext,
    expected_marks: state.marks,
    next_pending_mark: allCompleted ? null : deriveNextPendingMark(state.marks),
    all_marks_completed: allCompleted,
    next_schedule: state.nextSchedule,
    previous_schedule: state.previousSchedule,
  };
};

// ---------------------------------------------------------------------------
// Status computation
//
// Simplified version of what the backend does. Uses the effective_* fields
// and scheduled times to determine mark status. Good enough for testing
// — exact zone boundaries may differ slightly from real backend.
// ---------------------------------------------------------------------------

/** Computes the status for a mark based on when it was registered vs the schedule.
 * No shift or non-working day → always REGISTERED (no schedule to compare). */
export const computeMarkStatus = (
  markType: TimeAttendanceMarkType,
  shiftContext: TShiftContext | null,
): TimeAttendanceMarkStatus => {
  // No shift or rest day → no schedule to compare against
  if (!shiftContext || !shiftContext.is_working_day) {
    return TimeAttendanceMarkStatus.Registered;
  }

  const now = Date.now();

  if (markType === TimeAttendanceMarkType.Entry) {
    return computeEntryStatus(now, shiftContext);
  }

  if (markType === TimeAttendanceMarkType.Exit) {
    return computeExitStatus(now, shiftContext);
  }

  if (markType === TimeAttendanceMarkType.BreakStart) {
    return computeBreakStartStatus(now, shiftContext);
  }

  if (markType === TimeAttendanceMarkType.BreakEnd) {
    return computeBreakEndStatus(now, shiftContext);
  }

  return TimeAttendanceMarkStatus.OnTime;
};

// Small grace window around the scheduled time to count as ON_TIME.
// Without this, ON_TIME would require marking at the exact millisecond.
// Open question in user story — using 2 min as reasonable default.
const ON_TIME_GRACE_MS = 2 * 60_000;

// --- Entry status ---
// 5-zone model from user story:
// |--OUT_OF_SCHEDULE--|--ACCEPTABLE--|--ON_TIME--|--ACCEPTABLE--|--LATE--|
// ^                   ^              ^           ^              ^
// (too early)   effective_start  entry_time  entry+tol_after  (beyond)
//
// tolerance_before = entry_time - effective_shift_start (derived)
// tolerance_after  = effective_shift_end - exit_time (same config value, applied to entry)
const computeEntryStatus = (
  now: number,
  shift: TShiftContext,
): TimeAttendanceMarkStatus => {
  if (!shift.entry_time) {
    return TimeAttendanceMarkStatus.Registered;
  }

  const entryTime = new Date(shift.entry_time).getTime();
  const effectiveStart = shift.effective_shift_start
    ? new Date(shift.effective_shift_start).getTime()
    : entryTime;

  // tolerance_after for entry: use the same tolerance value (symmetric in our mock)
  const toleranceBefore = entryTime - effectiveStart;
  const toleranceAfter = toleranceBefore;

  if (now < effectiveStart) {
    return TimeAttendanceMarkStatus.OutOfSchedule;
  }

  if (now < entryTime - ON_TIME_GRACE_MS) {
    return TimeAttendanceMarkStatus.Acceptable;
  }

  // At or very near the scheduled time → ON_TIME
  if (now <= entryTime + ON_TIME_GRACE_MS) {
    return TimeAttendanceMarkStatus.OnTime;
  }

  // After scheduled but within tolerance_after → ACCEPTABLE (late but within tolerance)
  if (now <= entryTime + toleranceAfter) {
    return TimeAttendanceMarkStatus.Acceptable;
  }

  return TimeAttendanceMarkStatus.Late;
};

// --- Exit status ---
// 5-zone model (mirrored):
// |--EARLY--|--ACCEPTABLE--|--ON_TIME--|--ACCEPTABLE--|--OUT_OF_SCHEDULE--|
// ^         ^              ^           ^              ^
// (too early) exit-tol_before exit_time  effective_end  (beyond)
const computeExitStatus = (
  now: number,
  shift: TShiftContext,
): TimeAttendanceMarkStatus => {
  if (!shift.exit_time) {
    return TimeAttendanceMarkStatus.Registered;
  }

  const exitTime = new Date(shift.exit_time).getTime();
  const effectiveEnd = shift.effective_shift_end
    ? new Date(shift.effective_shift_end).getTime()
    : exitTime;

  // tolerance_before for exit: use the same tolerance value (symmetric in our mock)
  const toleranceAfter = effectiveEnd - exitTime;
  const toleranceBefore = toleranceAfter;

  if (now > effectiveEnd) {
    return TimeAttendanceMarkStatus.OutOfSchedule;
  }

  if (now > exitTime + ON_TIME_GRACE_MS) {
    return TimeAttendanceMarkStatus.Acceptable;
  }

  // At or very near the scheduled time → ON_TIME
  if (now >= exitTime - ON_TIME_GRACE_MS) {
    return TimeAttendanceMarkStatus.OnTime;
  }

  // Before scheduled but within tolerance_before → ACCEPTABLE (early but within tolerance)
  if (now >= exitTime - toleranceBefore) {
    return TimeAttendanceMarkStatus.Acceptable;
  }

  return TimeAttendanceMarkStatus.Early;
};

// --- Break start status ---
// Same 5-zone model as Entry:
// |--EARLY--|--ACCEPTABLE--|--ON_TIME--|--ACCEPTABLE--|--LATE--|
const computeBreakStartStatus = (
  now: number,
  shift: TShiftContext,
): TimeAttendanceMarkStatus => {
  const slot = shift.break_time_slots[0];

  if (!slot?.start_time) {
    return TimeAttendanceMarkStatus.OnTime;
  }

  const scheduled = new Date(slot.start_time).getTime();
  const effectiveStart = slot.effective_break_start
    ? new Date(slot.effective_break_start).getTime()
    : scheduled;

  const toleranceBefore = scheduled - effectiveStart;
  const toleranceAfter = toleranceBefore;

  if (now < effectiveStart) {
    return TimeAttendanceMarkStatus.Early;
  }

  if (now < scheduled - ON_TIME_GRACE_MS) {
    return TimeAttendanceMarkStatus.Acceptable;
  }

  if (now <= scheduled + ON_TIME_GRACE_MS) {
    return TimeAttendanceMarkStatus.OnTime;
  }

  if (now <= scheduled + toleranceAfter) {
    return TimeAttendanceMarkStatus.Acceptable;
  }

  return TimeAttendanceMarkStatus.Late;
};

// --- Break end status ---
// Same 5-zone model as Exit:
// |--EARLY--|--ACCEPTABLE--|--ON_TIME--|--ACCEPTABLE--|--LATE--|
const computeBreakEndStatus = (
  now: number,
  shift: TShiftContext,
): TimeAttendanceMarkStatus => {
  const slot = shift.break_time_slots[0];

  if (!slot?.end_time) {
    return TimeAttendanceMarkStatus.OnTime;
  }

  const scheduled = new Date(slot.end_time).getTime();
  const effectiveEnd = slot.effective_break_end
    ? new Date(slot.effective_break_end).getTime()
    : scheduled;

  const toleranceAfter = effectiveEnd - scheduled;
  const toleranceBefore = toleranceAfter;

  if (now > effectiveEnd) {
    return TimeAttendanceMarkStatus.Late;
  }

  if (now > scheduled + ON_TIME_GRACE_MS) {
    return TimeAttendanceMarkStatus.Acceptable;
  }

  if (now >= scheduled - ON_TIME_GRACE_MS) {
    return TimeAttendanceMarkStatus.OnTime;
  }

  if (now >= scheduled - toleranceBefore) {
    return TimeAttendanceMarkStatus.Acceptable;
  }

  return TimeAttendanceMarkStatus.Early;
};

// ---------------------------------------------------------------------------
// Mutation error mocks — activated by &mutation_mock=<key>
// ---------------------------------------------------------------------------

type TMockMutationError = {
  success: false;
  errorCode: string;
  message: string;
};

export const MUTATION_ERROR_MOCKS: Record<string, TMockMutationError> = {
  mutation_already_registered: {
    success: false,
    errorCode: 'MARK_ALREADY_REGISTERED',
    message: 'Esta marca ya fue registrada',
  },
  mutation_no_active_entry: {
    success: false,
    errorCode: 'NO_ACTIVE_ENTRY',
    message: 'No se encontró un ingreso activo',
  },
  mutation_network_error: {
    success: false,
    errorCode: 'NETWORK_ERROR',
    message: 'Error de conexión',
  },
  mutation_generic_error: {
    success: false,
    errorCode: 'INTERNAL_ERROR',
    message: 'Ocurrió un error interno',
  },
};
